import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/ui/card';
import { ArrowLeft, Leaf, Wrench, Truck, BarChart3, Users, CheckCircle, Zap, Target, Shield, ArrowRight } from 'lucide-react';

export default function DespoluirArticlePage() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1766608422198-5be9ac0aac9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx0cnVjayUyMGhpZ2h3YXklMjByb2FkJTIwZnJlaWdodCUyMHRyYW5zcG9ydGF0aW9ufGVufDB8fHx8MTc3NTUwNjg3OXww&ixlib=rb-4.1.0&q=85"
              alt="Frota de caminhões"
              className="w-full h-full object-cover opacity-15"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 to-gray-900" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors mb-8" data-testid="back-to-home">
              <ArrowLeft size={16} /> Voltar ao início
            </Link>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold tracking-wider rounded-full">
                SUSTENTABILIDADE
              </span>
              <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold tracking-wider rounded-full">
                PARCERIA
              </span>
            </div>
            <h1 data-testid="article-title" className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Projeto Rota da Eficiência Sustentável
            </h1>
            <p className="text-xl text-red-400 font-semibold mb-4">
              SSA DISTRIBUIDORA & Fetranscarga
            </p>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              O Programa Despoluir já transforma o transporte de cargas no Rio de Janeiro. Agora, com a SSA ao lado, cada diagnóstico ganha uma solução técnica imediata para manter sua frota rodando.
            </p>
            <div className="flex items-center justify-center gap-6 mt-8">
              <img
                src="https://customer-assets.emergentagent.com/job_9d67a82a-11ea-4ca7-ab70-beec0433a409/artifacts/ooz54jpy_SSA_MONO_BRANCO.png"
                alt="SSA Distribuidora"
                className="h-12 w-auto"
              />
              <div className="h-10 w-px bg-gray-600" />
              <div className="text-white font-bold text-lg">Fetranscarga</div>
              <div className="h-10 w-px bg-gray-600" />
              <div className="text-green-400 font-bold text-lg flex items-center gap-1">
                <Leaf size={18} /> Despoluir
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">

          {/* Visão Geral */}
          <section>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">O Despoluir funciona. Nós completamos o ciclo.</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                O Programa Despoluir, conduzido pela Fetranscarga, é uma referência nacional em sustentabilidade no transporte. Através da medição técnica de emissões (opacidade), ele identifica com precisão quais veículos precisam de atenção — um trabalho essencial que protege o meio ambiente e fortalece o setor.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                A SSA DISTRIBUIDORA entra como <strong>parceira técnica complementar</strong>: quando o diagnóstico aponta a necessidade de correção, nós entregamos a solução — da análise da causa-raiz até a peça certa, no prazo certo, para que o caminhão volte a rodar o mais rápido possível.
              </p>
              <p className="text-gray-900 font-semibold text-lg">
                Juntos, fechamos o ciclo: identificação + solução + frota rodando.
              </p>
            </div>
          </section>

          {/* Proposta de Valor */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white">
                <Target size={20} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">O Que Entregamos</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Uma parceria promissora que buscará melhorar a eficiência do transporte no Rio de Janeiro. Atuamos na decisão técnica certa que garante conformidade ambiental e a continuidade da sua operação.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-t-4 border-t-green-600 text-center">
                <CheckCircle className="mx-auto text-green-600 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">Conformidade</h3>
                <p className="text-sm text-gray-600">Aprovação no selo Despoluir com agilidade. Diagnóstico preciso, correção definitiva.</p>
              </Card>
              <Card className="p-6 border-t-4 border-t-red-600 text-center">
                <BarChart3 className="mx-auto text-red-600 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">Economia</h3>
                <p className="text-sm text-gray-600">Redução significativa no consumo de combustível através de combustão otimizada e manutenção inteligente.</p>
              </Card>
              <Card className="p-6 border-t-4 border-t-gray-700 text-center">
                <Truck className="mx-auto text-gray-700 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">Disponibilidade</h3>
                <p className="text-sm text-gray-600">Redução drástica do tempo de parada. Peças críticas disponíveis para entrega imediata.</p>
              </Card>
            </div>
          </section>

          {/* Ecossistema */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ecossistema da Parceria</h2>
            <p className="text-gray-600 mb-6">Cada parceiro contribui com sua especialidade. Juntos, criamos uma solução completa para o transportador.</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 text-center border-t-4 border-t-gray-700">
                <Users className="mx-auto text-gray-700 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">Fetranscarga</h3>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" /> Acesso direto às transportadoras do RJ</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" /> Validação institucional e credibilidade</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" /> Capacidade de escala em todo o estado</li>
                </ul>
              </Card>
              <Card className="p-6 text-center border-t-4 border-t-green-600">
                <Leaf className="mx-auto text-green-600 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">Programa Despoluir</h3>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Referência em sustentabilidade no transporte</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Medição técnica precisa de emissões</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Chancela ambiental reconhecida</li>
                </ul>
              </Card>
              <Card className="p-6 text-center border-t-4 border-t-red-600">
                <Wrench className="mx-auto text-red-600 mb-3" size={32} />
                <h3 className="font-bold text-gray-900 mb-2">SSA DISTRIBUIDORA</h3>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" /> Braço técnico e operacional da parceria</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" /> Diagnóstico de causa-raiz e plano de ação</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" /> Fornecimento imediato de peças e soluções</li>
                </ul>
              </Card>
            </div>
          </section>

          {/* Como Funciona */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Como Funciona na Prática</h2>
            <p className="text-gray-600 mb-6">Um fluxo simples e eficiente que transforma diagnóstico em ação, sem burocracia.</p>
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
              {[
                { num: '1', color: 'bg-green-600', title: 'Aferição Despoluir', desc: 'O técnico do Programa Despoluir realiza o teste de opacidade nas garagens da transportadora — o ponto de partida de todo o processo.' },
                { num: '2', color: 'bg-red-600', title: 'Laudo Técnico SSA', desc: 'Quando o veículo está fora do padrão, o consultor SSA entra em cena. Análise completa de Injeção, Arla 32, Turbina e Filtros para identificar a causa-raiz com precisão.' },
                { num: '3', color: 'bg-red-600', title: 'Solução e Fornecimento', desc: 'Com a causa identificada, fornecemos a peça certa e o plano de correção. Condições exclusivas para filiados à Fetranscarga. Sem troca de peças às cegas — cada ação é cirúrgica.' },
                { num: '4', color: 'bg-gray-700', title: 'Treinamento e Prevenção', desc: 'Workshops técnicos para mecânicos da transportadora sobre manutenção voltada à eficiência e redução contínua de emissões. Caminhão que não para é caminhão que fatura.' },
              ].map((step, i) => (
                <div key={i} className="relative flex items-start gap-4 mb-8 last:mb-0">
                  <div className={`relative z-10 flex-shrink-0 w-12 h-12 ${step.color} rounded-full flex items-center justify-center text-white font-bold`}>
                    {step.num}
                  </div>
                  <div className="pt-2">
                    <h3 className="font-bold text-gray-900">{step.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Onde Atuamos */}
          <section className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Onde a Expertise SSA Faz a Diferença</h2>
            <p className="text-gray-600 mb-6">Cada componente abaixo impacta diretamente na emissão, no consumo e na vida útil do motor. Conhecemos cada um a fundo.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Filtragem de Alta Performance',
                  desc: 'Ar puro na câmara de combustão é o primeiro passo. Filtros saturados são a causa primária de mistura rica e fumaça preta. Garantimos o componente certo para cada aplicação.',
                },
                {
                  title: 'Sistema de Injeção',
                  desc: 'Bicos e bombas injetoras calibrados significam combustão completa, menor consumo e aprovação garantida. Fornecemos peças originais e genuínas com rastreabilidade total.',
                },
                {
                  title: 'Tratamento de Gases',
                  desc: 'Sensores de NOx, filtros de partículas (DPF) e sistema de Arla 32 funcionando perfeitamente. A última linha de defesa entre o motor e o meio ambiente.',
                },
              ].map((item, i) => (
                <Card key={i} className="p-6 bg-white">
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Por que a SSA */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Por Que a SSA É o Parceiro Certo</h2>
            <p className="text-gray-600 mb-6">No setor de manutenção, uma peça barata que falha custa muito mais do que uma peça de qualidade. Na SSA, o compromisso com a sua produtividade é inegociável.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Shield, title: 'Procedência Inquestionável', desc: '100% do nosso estoque vem de fabricantes homologados ou canais oficiais. Cada componente possui certificação de origem e suporte direto da fábrica.' },
                { icon: Zap, title: 'Consultoria Antes da Venda', desc: 'Validamos sua demanda antes de vender. Ao entender a aplicação final, reduzimos drasticamente o índice de falhas por erro de especificação.' },
                { icon: Truck, title: 'Agilidade Logística (Eixo RJ–SP)', desc: 'Localização estratégica na Rod. Washington Luiz. Em casos críticos, nossa prioridade é a retomada imediata da sua operação. Máquina parada é prejuízo.' },
                { icon: Target, title: 'Foco Exclusivo em Linha Pesada', desc: 'Respiramos a rotina da indústria. Nossos prazos, linguagem e política de garantia são desenhados para quem não pode abrir mão de conformidade técnica.' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Resultados */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">O Que Sua Frota Ganha</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-8 text-center bg-green-700 text-white">
                <div className="text-4xl font-bold mb-2">100%</div>
                <p className="text-sm opacity-90">de aprovação no reteste do Programa Despoluir</p>
              </Card>
              <Card className="p-8 text-center bg-red-600 text-white">
                <div className="text-4xl font-bold mb-2">Até 15%</div>
                <p className="text-sm opacity-90">de redução no consumo de combustível</p>
              </Card>
              <Card className="p-8 text-center bg-gray-800 text-white">
                <div className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
                  <Truck size={32} />
                </div>
                <p className="text-sm opacity-90">Fim das paradas inesperadas e multas ambientais</p>
              </Card>
            </div>
            <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-3">Impacto completo para sua operação:</h3>
              <ul className="space-y-2">
                {[
                  'Redução drástica de custos com manutenção corretiva',
                  'Combustão otimizada = menor consumo de diesel',
                  'Aumento da vida útil dos componentes do motor',
                  'Regularização ambiental garantida (Selo Despoluir)',
                  'Caminhão rodando e faturando — não parado na oficina',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Frase de impacto */}
          <section className="text-center py-8">
            <p className="text-2xl font-bold text-gray-900 italic max-w-2xl mx-auto">
              "O Despoluir identifica. A SSA resolve. Sua frota não para."
            </p>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Quer fechar o ciclo da eficiência na sua frota?</h2>
            <p className="text-gray-300 mb-6 max-w-lg mx-auto">
              Fale com a SSA DISTRIBUIDORA. Vamos juntos transformar conformidade ambiental em performance mecânica e financeira.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#contato"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                Fale Conosco
                <ArrowRight size={18} />
              </Link>
              <a
                href="https://fetranscarga.org.br/despoluir/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-green-500 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                <Leaf size={18} />
                Conheça o Programa Despoluir
              </a>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </>
  );
}
